import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import type { Day } from '../../fixtures/plan';
import { useServerFlag, useServerFlagReady, type ServerFlagState } from '../../hooks/useServerFlag';
import type { MacroPlanStatus } from '../../hooks/useMacroPlan';
import { getCachedServerFlag, getServerFlags } from '../../lib/supabase';
import { isNativeApp } from '../../lib/platform';
import type { ProactiveTriggerType } from '../../lib/proactiveCopy';
import { proactiveTypeReadable } from '../../lib/proactiveKicker';
import { parseRunIdFromCoachHref } from '../../lib/run/runContextActivation';
import type { TodayLayout } from '../../lib/todayLayoutMemory';
import { tomorrowSessionLine } from '../../lib/todayHeroAdapter';
import styles from './skeletons.module.css';
// The stale row's own box (padding, border, flex-wrap, gaps, button): the placeholder
// borrows its classes, so the geometry cannot drift from the row it stands for.
import rowStyles from '../today/v2/StatusRow.module.css';
// W1-TODAY-FIX4: the same rule for the other blocks the loaded page draws. The Next steps
// card, the rest day's Tomorrow row, the coach line, the done day's Done and effort controls
// and the action column are drawn inside THEIR OWN boxes (classes borrowed, read only), so
// their paddings, gaps, minimum heights and wrapping rules cannot drift from the blocks
// they stand for. Only the contents are bars.
import stepStyles from '../today/v2/NextStepsCard.module.css';
import heroStyles from '../TodayHero/TodayHero.module.css';
import coachStyles from '../today/CoachLine.module.css';
import markStyles from '../today/MarkTodayCompleteButton.module.css';
import loopStyles from '../today/LoopHero.module.css';

import type { HealthRow } from '../today/v2/StatusRow';

/**
 * The stale row's sentence as bars. The row's text wraps with the width of its box, and
 * a phone's box holds two or three lines of it depending on the day it names, so a bar
 * per WORD, each as wide as the word it stands for, wraps inside the same flex box the
 * way the sentence does and the placeholder is as tall as the row. The widths are in em
 * of the row's text (the row sets its size from the ramp): each word set in DM Sans at
 * the row's real size (14px; 600 for the bold lead, 400 for the rest; the face has an
 * optical-size axis, so a word measured at 100px is about 8% too narrow) and read from
 * its box in a browser, to 0.001em. The days and months are the en-US names
 * `syncLongDayLabel` prints. Numbers, not text: nothing here is ever rendered as a word.
 * A change of the body font, or of the sentence, means measuring these again (the lane
 * report says how).
 */
/** The gap between two words: the bold space (0.244em) and the regular one (0.267em), averaged by count. */
const SPACE_EM = 0.258;
const LEAD_FIXED_EM = [2.835, 3.15, 1.754, 3.528]; // Apple Health last synced
/**
 * The second sentence, one width per word: `STALE_ROW_SECOND_SENTENCE` (Ask 3, string 16),
 * "Recent runs and sleep may be missing." The table used to be the retired string 22's ten words
 * ("Check that your other apps still share with Apple Health."), so the placeholder drew three
 * bars too many and wrapped a line early (B413, critic H1b). The test imports the real string
 * and pins this table to its word count: a change of the sentence fails it until it is measured
 * again.
 */
const REST_EM = [3.239, 2.019, 1.746, 2.489, 1.977, 1.195, 3.704]; // Recent runs and sleep may be missing.
const WEEKDAY_EM: Readonly<Record<string, number>> = {
  'Sunday,': 3.76, 'Monday,': 4.038, 'Tuesday,': 4.212, 'Wednesday,': 5.864,
  'Thursday,': 4.672, 'Friday,': 3.167, 'Saturday,': 4.529,
};
const MONTH_EM: Readonly<Record<string, number>> = {
  January: 3.873, February: 4.327, March: 3.035, April: 2.258, May: 2.02, June: 2.329,
  July: 1.978, August: 3.405, September: 5.377, October: 4.018, November: 4.984, December: 5.045,
};
const DIGIT_EM = [0.699, 0.349, 0.578, 0.599, 0.638, 0.619, 0.633, 0.537, 0.626, 0.633];
const PERIOD_EM = 0.237;
/** "Sync now" at 600, in em of the button's own text size (`--type-body`, 16px). */
const SYNC_NOW_EM = 4.622;
/** A word this table does not know (another locale's day name) is drawn at an average width. */
const FALLBACK_EM_PER_CHAR = 0.55;

/** The word widths of "Apple Health last synced {day}. Recent runs and sleep may be missing.", in em. */
export function staleSentenceWordsEm(day: string): number[] {
  const tokens = day.trim().split(/\s+/).filter(Boolean);
  const dayEm = tokens.map((token, i) => {
    const sentenceEnd = i === tokens.length - 1 ? PERIOD_EM : 0;
    const known = WEEKDAY_EM[token] ?? MONTH_EM[token];
    if (known !== undefined) return known + sentenceEnd;
    if (/^\d+$/.test(token)) return [...token].reduce((sum, d) => sum + DIGIT_EM[Number(d)]!, 0) + sentenceEnd;
    return token.length * FALLBACK_EM_PER_CHAR + sentenceEnd;
  });
  return [...LEAD_FIXED_EM, ...dayEm, ...REST_EM];
}

/** The word bar's height as a share of the font size; the line pitch is `--lh-body`. */
const WORD_BAR_EM = 0.7;
const WORD_ROW_GAP = `calc(1em * var(--lh-body) - ${WORD_BAR_EM}em)`;

/**
 * W1-TODAY-FIX4 (finding 1): the blocks the loaded page draws that the skeleton did not.
 *
 * The Next steps card wraps its rows with the width of its box (a step's copy and its action
 * sit side by side or stack, and the step's sentence takes two to four lines), so the
 * placeholder is built the way the stale row's is: the card's own boxes, one bar per WORD at
 * the width of the word it stands for, in the card's own flex-wrap. The widths are em of the
 * text's own size, each word set in DM Sans at that size (16px at 600 for a step's title and
 * its action, 14px at 400 for its sentence, 18px at 600 for the card's title) and read from
 * its box in a browser to 0.001em; the face has an optical-size axis, so a word measured at
 * another size is wrong by several percent. They are NUMBERS: nothing here is ever rendered
 * as a word. A change of the body font or of a step's sentence means measuring them again
 * (the lane report says how).
 */
export type SkeletonStep = 'approve' | 'draft' | 'add';
const TITLE_SPACE_EM = 0.244; // the bold space, in the step title
const WHO_SPACE_EM = 0.267; // the regular space, in the step sentence
const STEP_TITLE_EM: Readonly<Record<'approve' | 'add', readonly number[]>> = {
  approve: [4.076, 2.15, 3.419, 2.077], // Approve your season plan
  add: [1.965, 2.15, 1.996, 2.157], // Add your goal race
};
const DRAFT_LEAD_EM = 2.452; // Draft
const TITLE_WORD_EM: Readonly<Record<string, number>> = { weeks: 3.021, week: 2.524, to: 1.012, and: 1.823 };
const TITLE_DIGIT_EM = [0.7, 0.347, 0.576, 0.599, 0.638, 0.619, 0.633, 0.536, 0.626, 0.633];
const TITLE_COMMA_EM = 0.227;
const STEP_WHO_EM: Readonly<Record<SkeletonStep, readonly number[]>> = {
  approve: [2.128, 1.676, 1.69, 3.787, 0.83, 0.626, 3.565, 2.046, 3.301, 1.746, 1.676, 1.69, 1.558, 1.551, 1.283, 0.545, 1.885, 2.166],
  draft: [1.66, 2.273, 2.608, 2.108, 2.977, 2.259, 2.385, 3.028, 3.997, 0.742, 3.192],
  add: [1.66, 1.799, 0.83, 2.029, 1.968, 4.262, 3.262, 0.83],
};
/** The action's text (the pill is the card's own `.action`, 16px at 600). */
const STEP_ACTION_EM: Readonly<Record<SkeletonStep, number>> = { approve: 3.352, draft: 2.452, add: 1.965 };
/** "Next steps" at 18px and 600; "{n} left" at 14px, for n of 1, 2, 3. */
const NEXT_STEPS_HEAD_EM = 5.161;
const LEFT_COUNT_EM = [2.102, 2.366, 2.381];
/** A word this table does not know is drawn at an average width, like the stale row's. */
const FALLBACK_TITLE_EM_PER_CHAR = 0.55;

/** The word widths of a Draft step's title, "Draft weeks 7 to 10", in em. Exported for the test. */
export function draftTitleWordsEm(weeksLabel: string): number[] {
  const tokens = weeksLabel.trim().split(/\s+/).filter(Boolean);
  const labelEm = tokens.map((token) => {
    const known = TITLE_WORD_EM[token];
    if (known !== undefined) return known;
    return [...token].reduce(
      (sum, ch) => sum + (ch === ',' ? TITLE_COMMA_EM : /\d/.test(ch) ? TITLE_DIGIT_EM[Number(ch)]! : FALLBACK_TITLE_EM_PER_CHAR),
      0,
    );
  });
  return [DRAFT_LEAD_EM, ...labelEm];
}

/**
 * The coach line's text is the server's or the model's, so its words are not in a table; the
 * CHARACTERS are. A word is the sum of its characters' widths (DM Sans at 14px and 400, the
 * coach line's size, each character set 20 times in the coach line's own box and divided, in
 * em, for the printable ASCII from "!" to "~"), the gap between words is the regular space,
 * and a character outside the table is drawn at 0.55em. An average width per character was
 * tried first (0.52em, the mean of the letters) and missed a line on 3 of 18 fresh texts; the
 * table (with the kerning factor below) is the fix. The route passes the skeleton these WIDTHS, never the text.
 */
const COACH_CHAR_EM = [
  0.25, 0.291, 0.805, 0.58, 0.786, 0.734, 0.159, 0.373, 0.373, 0.48, 0.55, 0.182, 0.541, 0.198, 0.278, // ! to /
  0.684, 0.312, 0.576, 0.591, 0.607, 0.611, 0.629, 0.534, 0.609, 0.629, // 0 to 9
  0.202, 0.227, 0.55, 0.55, 0.55, 0.524, 1.006, // : to @
  0.664, 0.603, 0.717, 0.688, 0.565, 0.535, 0.758, 0.681, 0.234, 0.501, 0.584, 0.527, 0.842, // A to M
  0.691, 0.774, 0.58, 0.774, 0.594, 0.58, 0.561, 0.656, 0.669, 0.968, 0.603, 0.58, 0.541, // N to Z
  0.314, 0.261, 0.314, 0.622, 0.66, 0.213, // [ to `
  0.544, 0.627, 0.572, 0.627, 0.568, 0.34, 0.558, 0.575, 0.24, 0.243, 0.504, 0.223, 0.891, // a to m
  0.574, 0.593, 0.627, 0.627, 0.37, 0.502, 0.392, 0.573, 0.527, 0.763, 0.496, 0.556, 0.458, // n to z
  0.427, 0.234, 0.427, 0.55, // { to ~
];
const COACH_FALLBACK_CHAR_EM = 0.55;
/** Kerning: a whole line set in the face is 0.8% narrower than its characters summed (19 lines: 0.985 to 0.996, sd 0.003). */
const COACH_KERNING = 0.992;
/** The middle dot between the coach's name and the line, and the name ("Airo" at 600). */
const COACH_DOT_EM = 0.198;
const COACH_NAME_EM: Readonly<Record<string, number>> = { Airo: 1.951 };

function coachCharEm(ch: string): number {
  const code = ch.charCodeAt(0);
  return code >= 33 && code <= 126 ? COACH_CHAR_EM[code - 33]! : COACH_FALLBACK_CHAR_EM;
}
/** A word's width in em at the coach line's size. Exported for the test. */
export function coachWordEm(word: string): number {
  return [...word].reduce((sum, ch) => sum + coachCharEm(ch), 0) * COACH_KERNING;
}

/** What the loaded hero's day is (W1-TODAY-FIX4): drives the text lines and the controls. */
export type SkeletonHero =
  | { kind: 'train' }
  | { kind: 'rest'; tomorrow: boolean }
  | { kind: 'done'; lines: number; effort: 'add' | 'edit' };
const TRAIN: SkeletonHero = { kind: 'train' };
const NO_STEPS: readonly SkeletonStep[] = [];
/** "Add effort" and "Edit effort" at 16px and 600, the effort action's text. */
const EFFORT_EM = { add: 4.923, edit: 4.865 } as const;

/** The coach line the loaded hero will draw (the echo, or the proactive RELATED line). */
export interface SkeletonCoachLine {
  kind: 'echo' | 'related';
  /** Each word's width in em, in order (the echo's first two are the coach's name and the dot). */
  words: readonly number[];
}

function wordsBox(spaceEm: number): CSSProperties {
  return { display: 'flex', flexWrap: 'wrap', columnGap: `${spaceEm}em`, rowGap: WORD_ROW_GAP, paddingBottom: WORD_ROW_GAP };
}
function WordBars({ em }: { em: readonly number[] }) {
  return (
    <>
      {em.map((w, i) => (
        <span key={i} className={styles.bar} style={{ flex: 'none', width: `${w}em`, height: `${WORD_BAR_EM}em` }} />
      ))}
    </>
  );
}

/**
 * B413 (critic H1): the Add location prompt's place. With no stored location the hero's Weather
 * slot asks for one (Ask 3, strings 12 to 14: the label, a line that wraps, and two actions each
 * at least `--hit-min` tall), about 105 px at 375 where the weather row is 24, and a frame that
 * left it out dropped the week card at the swap on every cold load. Drawn in the prompt's OWN
 * boxes (its classes, borrowed, read only): the label's line box, the line as a bar per word in
 * the line's own flex-wrap, and the two action boxes with a bar each. The widths are em of the
 * text's own size (14px; the line at 400, "Add location" at 600, "Not now" at 500; the label at 12px
 * and 600, uppercase), each set in DM Sans at that size and read from its box in a browser to
 * 0.001em. They are NUMBERS: nothing here is ever rendered as a word. A change of strings 12 to
 * 14 means measuring them again (the test imports the real strings and pins the counts).
 */
const PROMPT_LABEL_EM = 5.128; // WEATHER, in em of the label
const PROMPT_LINE_EM = [1.892, 2.046, 3.717, 0.972, 1.64, 1.535, 3.747, 1.283, 2.046, 2.218]; // Add your location to see the weather for your runs.
const PROMPT_ADD_EM = 6.124; // Add location
const PROMPT_NOT_NOW_EM = 3.891; // Not now
function WeatherPromptPlace() {
  return (
    <div className={heroStyles.v2WeatherPrompt} aria-hidden>
      <span
        className={heroStyles.v2Label}
        style={{ display: 'flex', alignItems: 'center', height: 'calc(var(--type-label) * var(--lh-label))' }}
      >
        <span className={styles.bar} style={{ width: `${PROMPT_LABEL_EM}em`, height: '0.7em' }} />
      </span>
      <span className={heroStyles.v2WeatherPromptLine} style={wordsBox(WHO_SPACE_EM)}>
        <WordBars em={PROMPT_LINE_EM} />
      </span>
      <div className={heroStyles.v2WeatherPromptActions}>
        <span className={heroStyles.v2WeatherAdd} style={{ pointerEvents: 'none' }}>
          <span className={styles.bar} style={{ width: `${PROMPT_ADD_EM}em`, height: '0.7em' }} />
        </span>
        <span className={heroStyles.v2WeatherNotNow} style={{ pointerEvents: 'none' }}>
          <span className={styles.bar} style={{ width: `${PROMPT_NOT_NOW_EM}em`, height: '0.7em' }} />
        </span>
      </div>
    </div>
  );
}

/** The Next steps card's place: its own boxes, a bar per word, in the card's own order. */
function NextStepsPlace({
  steps,
  draftWeeksLabel,
}: {
  steps: readonly SkeletonStep[];
  draftWeeksLabel: string | null;
}) {
  if (steps.length === 0) return null;
  const titleEm = (step: SkeletonStep): readonly number[] =>
    step === 'draft' ? draftTitleWordsEm(draftWeeksLabel ?? '') : STEP_TITLE_EM[step];
  return (
    <div className={stepStyles.card} aria-hidden>
      <div className={stepStyles.head} style={{ alignItems: 'center' }}>
        <span
          className={stepStyles.title}
          style={{ display: 'flex', alignItems: 'center', height: 'calc(var(--type-title) * var(--lh-title))' }}
        >
          <span className={styles.bar} style={{ width: `${NEXT_STEPS_HEAD_EM}em`, height: '0.7em' }} />
        </span>
        <span
          className={stepStyles.count}
          style={{ display: 'flex', alignItems: 'center', height: 'calc(var(--type-body-sm) * var(--lh-body))' }}
        >
          <span
            className={styles.bar}
            style={{ width: `${LEFT_COUNT_EM[Math.min(steps.length, 3) - 1]}em`, height: '0.7em' }}
          />
        </span>
      </div>
      <div className={stepStyles.list}>
        {steps.map((step) => (
          <div key={step} className={stepStyles.row}>
            <div className={stepStyles.copy}>
              <span className={stepStyles.stepTitle} style={wordsBox(TITLE_SPACE_EM)}>
                <WordBars em={titleEm(step)} />
              </span>
              <span className={stepStyles.who} style={wordsBox(WHO_SPACE_EM)}>
                <WordBars em={STEP_WHO_EM[step]} />
              </span>
            </div>
            <span className={stepStyles.action} style={{ pointerEvents: 'none' }}>
              <span className={styles.bar} style={{ width: `${STEP_ACTION_EM[step]}em`, height: '1em' }} />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * The coach line's place, inside the coach line's own box (`.coachLine.v2`, and the banner
 * box of `.isProactive` for the RELATED line, with its colour taken off: bars only).
 */
function CoachLinePlace({ line }: { line: SkeletonCoachLine }) {
  const related = line.kind === 'related';
  return (
    <div
      className={`${coachStyles.coachLine} ${coachStyles.v2}${related ? ` ${coachStyles.isProactive}` : ''}`}
      aria-hidden
      style={{ background: 'transparent', borderLeftColor: 'transparent', pointerEvents: 'none' }}
    >
      {!related && <span className={styles.bar} style={{ flex: 'none', width: 8, height: 8, borderRadius: '50%' }} />}
      <span className={coachStyles.txt}>
        {related && (
          <span
            className={coachStyles.v2Kick}
            style={{ display: 'flex', alignItems: 'center', height: 'calc(var(--type-label) * var(--lh-label))' }}
          >
            <span className={styles.bar} style={{ width: '11em', height: '0.7em' }} />
          </span>
        )}
        <span style={wordsBox(WHO_SPACE_EM)}>
          <WordBars em={line.words} />
        </span>
      </span>
    </div>
  );
}

/**
 * W1-TODAY-WIRE (spec 7.8 TWR-6, frame 1r, string 87) — the ONE cold-load
 * placeholder for the 2.0 Today.
 *
 * With `today_2_0_v1` on, Today holds a single gate until the session, week and
 * race reads have all settled, then swaps once. This is what shows while the gate
 * is closed: the date line and the hero card, then a week card with seven rows (and,
 * from W1-TODAY-FIX4, the Next steps card, the coach line and the rest or done day's
 * hero, when the route knows the loaded page will draw them). Nothing below the week
 * card is drawn, so the race card (and everything below it) arrives under the week
 * card without moving the week card's top.
 *
 * Contract:
 *   - `role="status"` + `aria-busy="true"` + the accessible name "Loading today"
 *     (Appendix B, string 87), once, on the root. Everything inside is decorative.
 *   - Neutral bars only, and no text of any kind: never a zero count, never
 *     "0 of 0 sessions", never another athlete's session (Phase 5 plan,
 *     Decision 4).
 *   - The geometry mirrors the loaded 2.0 hero card (`TodayHero.module.css`,
 *     `.v2Card`) and week card (`WeekListV2.module.css`): the same paddings, gaps
 *     and radii, and the bar heights are the loaded line boxes computed from the
 *     same ramp tokens, so a large text size grows the placeholder with the page.
 *   - The action area follows the loaded hero's own rule (`HeroActionsV2`): on the
 *     native app with `run_capture_v1` on it is Start run, its helper line and the
 *     quiet Mark done; everywhere else it is the one Mark done button. The day's
 *     text is drawn as two lines, the length of the frame's own example.
 *   - `gutter` adds the phone's 16px side padding (the mount supplies it on a
 *     phone; the desktop main column has none).
 *   - `weatherRow` (W1-TODAY-FIX1, F3) draws the weather row's place and the Apple
 *     Weather mark's under it, and is passed while a weather fetch is in flight for a
 *     stored location, and after it has resolved with a row to show (the route knows:
 *     `useHeroWeather().loading || data`; W1-TODAY-FIX2, R1: the placeholder must not
 *     vanish when weather lands before the session or week reads). Without a stored
 *     location, or with weather off or failed, the loaded hero has no such row and the
 *     skeleton draws none, so the week card's top is where the loaded page puts it.
 *   - `weatherPrompt` (B413) holds the place of the Add location prompt, the weather slot's
 *     other face: an athlete with no stored location who has not pressed Not now. The hero
 *     draws it unless the day is a rest day or a weather row is there, and so does the frame
 *     (it never draws both). From the layout memory; absent is the base case, byte for byte.
 *   - `healthRow` (W1-TODAY-FIX2, R2) holds the place of the Apple Health row the
 *     loaded page will draw, once the route knows which (`healthRowFor`): `connect` is
 *     the never-synced entry, inside the hero card after the action area; `stale` is the
 *     status row, between the hero and the week card, and carries the day it names so
 *     the sentence's bars wrap as the sentence does. Null (not yet known, or nothing
 *     will be drawn) draws nothing. Still bars only, and no text: the sentence is word
 *     bars inside the row's own flex box (its classes, borrowed), and the button is a
 *     bar inside the row's own button box.
 *   - `hero`, `nextSteps` and `coachLine` (W1-TODAY-FIX4, finding 1) hold the places of the
 *     blocks the loaded page draws in the states the base case is not: the hero's day
 *     (a rest day has no stat, one line of text, the Tomorrow row and NO action column; a
 *     done day has no day text, its lines and the Done and effort controls in their own
 *     wrapping row), the Next steps card with the steps it will list, and the coach line
 *     (the post-run echo or the proactive RELATED line). The route knows all three by gate
 *     time (`coldSkeletonPlace`); absent is the base case, byte for byte.
 *
 * W1-TODAY-LAYOUT-MEMORY: this is the FULL frame, drawn from the props above. The exported
 * `TodaySkeletonV2` (below, after this function) decides whether the route draws it from the
 * device's remembered layout or draws the plain frame instead.
 */
function SkeletonFrame({
  gutter = false,
  weatherRow = false,
  weatherPrompt = false,
  healthRow = null,
  hero = TRAIN,
  nextSteps = NO_STEPS,
  draftWeeksLabel = null,
  coachLine = null,
  startRun: rememberedStartRun,
}: {
  gutter?: boolean;
  weatherRow?: boolean;
  weatherPrompt?: boolean;
  healthRow?: HealthRow | null;
  hero?: SkeletonHero;
  nextSteps?: readonly SkeletonStep[];
  draftWeeksLabel?: string | null;
  coachLine?: SkeletonCoachLine | null;
  /** The remembered action area (W1-TODAY-LAYOUT-MEMORY); absent reads the flag as it always did. */
  startRun?: boolean;
}) {
  // Read like the hero's action area reads them, so the placeholder is as tall as
  // the controls that will replace it. (Hook first: never conditional.) A REMEMBERED layout
  // carries the answer instead: the flags are not in the page until the config read answers,
  // which is mid-load, and a frame that read them would grow Start run's lines when it did.
  const captureOn = useServerFlag('run_capture_v1');
  const startRun = rememberedStartRun ?? (isNativeApp() && captureOn);

  return (
    <div
      className={`${styles.todaySkelV2}${gutter ? ` ${styles.todaySkelV2Gutter}` : ''}`}
      role="status"
      aria-busy="true"
      aria-label="Loading today"
    >
      <div className={styles.todaySkelV2Hero} aria-hidden>
        <span className={`${styles.bar} ${styles.todaySkelV2Date}`} />
        <div className={styles.todaySkelV2Card}>
          <span className={`${styles.bar} ${styles.todaySkelV2Kicker}`} />
          <span className={`${styles.bar} ${styles.todaySkelV2Title}`} />
          {hero.kind !== 'rest' && <span className={`${styles.bar} ${styles.todaySkelV2Stat}`} />}
          {hero.kind === 'train' && (
            <div className={styles.todaySkelV2Text}>
              <span className={`${styles.bar} ${styles.todaySkelV2TextLine}`} />
              <span className={`${styles.bar} ${styles.todaySkelV2TextLine}`} />
            </div>
          )}
          {/* A rest day's one line ("Nothing to mark today."), a done day's actual and effort lines. */}
          {Array.from({ length: hero.kind === 'rest' ? 1 : hero.kind === 'done' ? hero.lines : 0 }, (_, i) => (
            <div key={i} className={styles.todaySkelV2Line}>
              <span className={`${styles.bar} ${styles.todaySkelV2LineBar}`} />
            </div>
          ))}
          {weatherRow && <span className={`${styles.bar} ${styles.todaySkelV2Weather}`} />}
          {/* The slot's other face: the hero's own rule (`!weather && prompt && !restLine`). */}
          {weatherPrompt && !weatherRow && hero.kind !== 'rest' && <WeatherPromptPlace />}
          {hero.kind === 'rest' && hero.tomorrow && (
            <div className={heroStyles.v2Tomorrow}>
              <span
                className={heroStyles.v2Label}
                style={{ display: 'flex', alignItems: 'center', height: 'calc(var(--type-label) * var(--lh-label))' }}
              >
                <span className={styles.bar} style={{ width: '5em', height: '0.7em' }} />
              </span>
              <span
                className={heroStyles.v2TomorrowValue}
                style={{ display: 'flex', alignItems: 'center', height: 'calc(var(--type-body) * var(--lh-body))' }}
              >
                <span className={styles.bar} style={{ width: '45%', height: '0.7em' }} />
              </span>
            </div>
          )}
          {hero.kind === 'train' && (
            <div className={styles.todaySkelV2Actions}>
              <span className={`${styles.bar} ${styles.todaySkelV2Button}`} />
              {startRun && (
                <>
                  <span className={`${styles.bar} ${styles.todaySkelV2Helper}`} />
                  <span className={`${styles.bar} ${styles.todaySkelV2Quiet}`} />
                </>
              )}
            </div>
          )}
          {hero.kind === 'done' && (
            // The done day's controls: the Done button (flex 1 1 12em) and the effort action
            // in the row's own flex-wrap, so the two stack at the width where the real ones do.
            <div className={styles.todaySkelV2Actions}>
              <div className={markStyles.v2Row}>
                <span
                  className={styles.bar}
                  style={{ flex: '1 1 12em', minHeight: 48, borderRadius: 'var(--r-pill)', fontSize: 'var(--type-body)' }}
                />
                <span className={loopStyles.v2Effort} style={{ pointerEvents: 'none' }}>
                  <span className={styles.bar} style={{ width: `${EFFORT_EM[hero.effort]}em`, height: '1em' }} />
                </span>
              </div>
            </div>
          )}
          {coachLine && <CoachLinePlace line={coachLine} />}
          {healthRow?.kind === 'connect' && (
            // The never-synced entry, a card child after the action area: the quiet
            // action's 44px box, left-aligned at the entry's own minimum width.
            <span
              className={`${styles.bar} ${styles.todaySkelV2Quiet}`}
              style={{ alignSelf: 'flex-start', width: 'var(--readiness-min-w)' }}
            />
          )}
          {weatherRow && <span className={`${styles.bar} ${styles.todaySkelV2Legal}`} />}
        </div>
      </div>
      {healthRow?.kind === 'stale' && (
        <div className={rowStyles.row} aria-hidden>
          <span
            className={styles.bar}
            style={{ flex: 'none', width: 8, height: 8, borderRadius: '50%' }}
          />
          <span
            className={rowStyles.text}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              columnGap: `${SPACE_EM}em`,
              rowGap: WORD_ROW_GAP,
              paddingBottom: WORD_ROW_GAP,
            }}
          >
            {staleSentenceWordsEm(healthRow.day).map((em, i) => (
              <span
                key={i}
                className={styles.bar}
                style={{ flex: 'none', width: `${em}em`, height: `${WORD_BAR_EM}em` }}
              />
            ))}
          </span>
          <span className={rowStyles.sync} style={{ pointerEvents: 'none' }}>
            <span className={styles.bar} style={{ width: `${SYNC_NOW_EM}em`, height: '1em' }} />
          </span>
        </div>
      )}
      <NextStepsPlace steps={nextSteps} draftWeeksLabel={draftWeeksLabel} />
      <div className={styles.todaySkelV2Week} aria-hidden>
        <div className={styles.todaySkelV2WeekHead}>
          <span className={`${styles.bar} ${styles.todaySkelV2WeekTitle}`} />
          <span className={`${styles.bar} ${styles.todaySkelV2WeekTotal}`} />
        </div>
        <span className={`${styles.bar} ${styles.todaySkelV2Track}`} />
        <div className={styles.todaySkelV2Rows}>
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className={styles.todaySkelV2Item}>
              <div className={styles.todaySkelV2Row}>
                <span className={`${styles.bar} ${styles.todaySkelV2Day}`} />
                <span className={`${styles.bar} ${styles.todaySkelV2Session}`} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * W1-TODAY-LAYOUT-MEMORY (option A, founder ruling 2026-10-18) - the plain frame: the date
 * bar and an empty column, under the one name "Loading today". What a device with no
 * remembered layout draws: no hero, Next steps or week card boxes, so nothing in it can be
 * inserted, removed or resized by a read that answers, and the page then arrives whole.
 */
function PlainFrame({ gutter }: { gutter: boolean }) {
  return (
    <div
      className={`${styles.todaySkelV2}${gutter ? ` ${styles.todaySkelV2Gutter}` : ''}`}
      role="status"
      aria-busy="true"
      aria-label="Loading today"
    >
      <div className={styles.todaySkelV2Hero} aria-hidden>
        <span className={`${styles.bar} ${styles.todaySkelV2Date}`} />
      </div>
    </div>
  );
}

/**
 * The 2.0 loading frame. `layout` says what the cold-load route knows from the device:
 *
 *   - `undefined` (every caller before this lane, and the skeleton's own tests): the full
 *     frame from the props, exactly as before.
 *   - a `TodayLayout`: the full frame drawn from what the last visit drew. The route passes
 *     it from the first paint to the swap and nothing the reads answer changes it, so no
 *     block is inserted or removed while the page loads (frames 1a to 1d as Claude Design
 *     drew them: ordinary, rest day, Next steps, coach line). The layout's fields ARE the
 *     props above, so the same boxes are drawn.
 *   - `null`: no usable memory, the plain frame.
 */
export function TodaySkeletonV2({
  layout,
  ...props
}: {
  gutter?: boolean;
  weatherRow?: boolean;
  weatherPrompt?: boolean;
  healthRow?: HealthRow | null;
  hero?: SkeletonHero;
  nextSteps?: readonly SkeletonStep[];
  draftWeeksLabel?: string | null;
  coachLine?: SkeletonCoachLine | null;
  layout?: TodayLayout | null;
}) {
  if (layout === undefined) return <SkeletonFrame {...props} />;
  if (layout === null) return <PlainFrame gutter={!!props.gutter} />;
  return <SkeletonFrame gutter={props.gutter} {...layout} />;
}

/**
 * W1-TODAY-FIX4 (finding 1) — what the route tells the skeleton, from values it already
 * holds by the time the gate closes. Each field is the SAME decision the loaded block
 * makes, written once more here because the blocks (`NextStepsCard`, `LoopHero`,
 * `CoachLine`) decide inside themselves; the contract tests render the real blocks across
 * the whole truth table and pin that this agrees with them. A value the route does not know
 * yet reserves nothing (the race read still open is `noGoalRace: false`), exactly as the
 * weather and Apple Health rows do.
 *
 * W1-TODAY-LAYOUT-MEMORY: the route no longer feeds this to the loading frame (a guess from
 * the reads that have answered inserted blocks mid-load). It is the memory's WRITER: once the
 * real page has landed and every read has settled, the route runs this over the same values
 * and keeps the result (`lib/todayLayoutMemory`) for the next visit's frame.
 */
export interface ColdSkeletonInput {
  /** `useMacroPlan().status`. */
  approvalStatus: MacroPlanStatus | undefined;
  /** The weeks the next draft would author ("weeks 7 to 10"), null when none. */
  draftWeeksLabel: string | null;
  /** `draft_week_journey_v1`. */
  draftJourneyOn: boolean;
  /** No race is on the calendar AND the race read has settled. */
  noGoalRace: boolean;
  /** Today's row of the week (`weekPlan.days.find((d) => d.today)`), null while the week is unknown. */
  today: Pick<Day, 'rest' | 'completionStatus' | 'rpe' | 'actual'> | null | undefined;
  /** The loaded week's days (the rest day's Tomorrow row reads them). */
  days: readonly Day[];
  /** The hero's coach line and where it points, with the trigger the route detected. */
  coachLine: string | null | undefined;
  coachHref: string | undefined;
  proactiveType: ProactiveTriggerType | null | undefined;
  /** The coach's display name (the echo prints it); `CoachLine`'s default is Airo. */
  coachName?: string;
}

export function coldSkeletonPlace(input: ColdSkeletonInput): {
  hero: SkeletonHero;
  nextSteps: SkeletonStep[];
  draftWeeksLabel: string | null;
  coachLine: SkeletonCoachLine | null;
} {
  const { today } = input;
  let hero: SkeletonHero = TRAIN;
  if (today?.rest) {
    hero = { kind: 'rest', tomorrow: !!tomorrowSessionLine(input.days as Day[]) };
  } else if (today?.completionStatus === 'completed') {
    hero = {
      kind: 'done',
      lines: (today.actual?.hero ? 1 : 0) + (typeof today.rpe === 'number' ? 1 : 0),
      effort: today.rpe == null ? 'add' : 'edit',
    };
  }

  // The same three rules as `NextStepsCard`, in its order.
  const nextSteps: SkeletonStep[] = [];
  if (input.approvalStatus === 'proposed') nextSteps.push('approve');
  const label = input.draftWeeksLabel?.trim() ?? '';
  if (input.draftJourneyOn && label !== '') nextSteps.push('draft');
  if (input.noGoalRace) nextSteps.push('add');

  // The same two tests as `CoachLine variant="v2"`: the echo is the line whose href carries a run,
  // the proactive line is a non-echo line with a readable trigger; anything else prints nothing.
  let coachLine: SkeletonCoachLine | null = null;
  if (input.coachLine) {
    const isEcho = parseRunIdFromCoachHref(input.coachHref) !== null;
    const related = proactiveTypeReadable(input.proactiveType ?? null);
    if (isEcho || related) {
      const words = input.coachLine.split(/\s+/).filter(Boolean).map(coachWordEm);
      const name = input.coachName ?? 'Airo';
      const nameEm = COACH_NAME_EM[name] ?? coachWordEm(name) * 1.04;
      coachLine = isEcho
        ? { kind: 'echo', words: [nameEm, COACH_DOT_EM, ...words] }
        : { kind: 'related', words };
    }
  }
  return { hero, nextSteps, draftWeeksLabel: label !== '' ? label : null, coachLine };
}

/**
 * W1-TODAY-COLDLOAD — WHICH TODAY IS THIS, DECIDED BEFORE ANY PAGE MOUNTS.
 *
 * The fourth attempt on the cold-load class. FIX1, FIX2 and FIX4 made the 2.0 page's own
 * gate right and left the PAGE deciding before it knew: a flag nobody has answered yet
 * reads false, the guard and the route's loading states read that false, and every cold
 * load painted the legacy skeleton for the length of the `/api/config` read (0.3 to 0.7 s
 * on dev) before the 2.0 one (UX pass 2026-10-17, defect 1). B87's lesson: decide the
 * frame before the page mounts.
 *
 *   · 'v2'      the flag is on, answered, or this device's cached value says so: the 2.0
 *               loading frame, then the 2.0 page.
 *   · 'legacy'  the flag is off, answered, or this device's cached value says so (or the
 *               last config the module is retrying on does): everything exactly as before
 *               the flag existed, the legacy skeleton from the first commit. This is every
 *               prod device today.
 *   · 'neutral' the read is open and this device holds NO cached flags at all (a first-ever
 *               visit): the page background and the one status "Loading today"
 *               (`TodayNeutralFrame`). Neither Today is built, so neither is replaced.
 *               PROVISIONAL (founder decision 2026-10-18, to be confirmed on a device).
 */
export type TodayFrame = 'neutral' | 'v2' | 'legacy';

/** The flag's id, for the two reads that are not a hook (the hook call below spells it out: the config-export rake scans for the literal). */
const TODAY_V2_FLAG = 'today_2_0_v1';

/**
 * Which Today the flag state says. Pure over the hook's `ready` and the module's own flag
 * cache (`lib/supabase`: `getServerFlags`, `getCachedServerFlag`).
 */
export function resolveTodayFrame(flag: ServerFlagState): TodayFrame {
  // Answered: a flags map arrived, or the bounded wait ended on what this device holds.
  if (flag.ready) return flag.enabled ? 'v2' : 'legacy';
  // The read is open. A value the module already holds decides at once: while it retries it
  // reports the last cached flags (`getServerFlags`), and a key in that map is a real answer
  // this deployment gave, on OR off.
  const held = getServerFlags();
  if (Object.prototype.hasOwnProperty.call(held, TODAY_V2_FLAG)) {
    return held[TODAY_V2_FLAG] ? 'v2' : 'legacy';
  }
  // Nothing in memory yet (the first read has not come back): a returning visitor is not
  // made to wait, on or off. The persisted config's value decides at once; only a device with
  // no cached flags at all (undefined) has nothing to go on.
  const cached = getCachedServerFlag(TODAY_V2_FLAG);
  return cached === undefined ? 'neutral' : cached ? 'v2' : 'legacy';
}

/**
 * The longest the neutral frame is held, in ms. PROVISIONAL (coordinator, Fable wave 1 sign-off
 * 2026-10-18; to be confirmed on a device): 2 s matches the landing's `LANDING_HOLD_CAP_MS` ruling
 * (`routes/landing.tsx`). Past it the flag fails closed to OFF, as every other reader of a flag
 * does, so a device with no cache and a config read that never settles gets the legacy Today
 * instead of a blank page.
 */
export const TODAY_NEUTRAL_CAP_MS = 2000;

/**
 * The frame, for the length of this component's life. ONE WAY: once the page has left
 * 'neutral' it never goes back, even if `retryConfigRead` unsettles the flags again (it does
 * that when a cycle gave up on the empty map), because a page that has already decided must
 * not be blanked by a later re-ask. A hook: callers place it above every early return.
 *
 * THE CEILING (also one way): after `TODAY_NEUTRAL_CAP_MS` of 'neutral' the frame latches
 * 'legacy'. That counts as a decision, so the same rule holds after it: a re-ask never blanks
 * the page, and a LATER settled answer still wins, once (a settled ON swaps the legacy page for
 * the 2.0 one, as the flip-day path already does). The timer belongs to this mount: it is
 * cleared on unmount and when the flag answers first.
 */
export function useTodayFrame(): TodayFrame {
  const flag = useServerFlagReady('today_2_0_v1');
  const decided = useRef<TodayFrame | null>(null);
  const [capped, setCapped] = useState(false);
  const frame = resolveTodayFrame(flag);
  const waiting = frame === 'neutral' && decided.current === null && !capped;
  useEffect(() => {
    if (!waiting) return;
    const timer = setTimeout(() => setCapped(true), TODAY_NEUTRAL_CAP_MS);
    return () => clearTimeout(timer);
  }, [waiting]);
  if (frame !== 'neutral') {
    decided.current = frame;
    return frame;
  }
  if (decided.current !== null) return decided.current;
  if (capped) {
    decided.current = 'legacy';
    return 'legacy';
  }
  return 'neutral';
}

/**
 * The neutral frame: the page background, the root status named "Loading today" (the 2.0
 * loading frame's own name, so no new string), no bars and no chrome.
 */
export function TodayNeutralFrame() {
  return (
    <div
      className={styles.todayNeutral}
      role="status"
      aria-busy="true"
      aria-label="Loading today"
    />
  );
}

/**
 * A loading state that has two real faces: the neutral frame while the flag is unanswered
 * and uncached, `v2` with the flag on, `legacy` with it off. Both faces are passed as
 * elements (cheap to build, mounted only when chosen), so a caller adds no hook of its own.
 */
export function TodayFrameSwitch({ v2, legacy }: { v2: ReactNode; legacy: ReactNode }) {
  const frame = useTodayFrame();
  if (frame === 'neutral') return <TodayNeutralFrame />;
  return <>{frame === 'v2' ? v2 : legacy}</>;
}

/**
 * The same decision for a page that is the same element either way (the route's happy path,
 * which reads the flag itself, `v2On`): the neutral frame while the flag is unanswered and
 * uncached, the children untouched otherwise (a fragment, no element of its own).
 *
 * `v2On` is the page's OWN reading of the flag, and the page is built only when it agrees with
 * the frame. They differ in exactly one window: this device's cached copy says ON (the frame is
 * 'v2') while the module has not yet been told anything (`v2On` is the bare false of an
 * unanswered flag). The page built in that window would be the legacy one, so it waits, as
 * neutral, for the read that settles it. It never waits past the ceiling (`useTodayFrame`): the
 * frame latches 'legacy' after `TODAY_NEUTRAL_CAP_MS`, and then the unanswered flag's bare false
 * AGREES with it, so the legacy page is built instead of a blank one.
 */
export function TodayFrameGate({ v2On, children }: { v2On: boolean; children: ReactNode }) {
  const frame = useTodayFrame();
  if (frame === 'neutral' || (frame === 'v2') !== v2On) return <TodayNeutralFrame />;
  return <>{children}</>;
}
